import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer, View, SlotInfo } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import {
  Calendar as CalendarIcon, Plus, Filter, Settings, ArrowLeftRight,
  X, Check, Trash2, Edit, RefreshCw, BarChart3, Save, AlertTriangle
} from 'lucide-react';
import { useToast, useConfirm } from '../components/ui';
import './TurnisticaPage.css';

moment.locale('it');
const localizer = momentLocalizer(moment);

const messages = {
  allDay: 'Tutto il giorno',
  previous: '◀',
  next: '▶',
  today: 'Oggi',
  month: 'Mese',
  week: 'Settimana',
  day: 'Giorno',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Ora',
  event: 'Evento',
  noEventsInRange: 'Nessun turno in questo periodo',
  showMore: (total: number) => `+ Altri ${total}`
};

// Tipi
interface Turno {
  id: number;
  utente_id: number;
  punto_vendita_id: number | null;
  mansione_id: number | null;
  data: string;
  ora_inizio: string;
  ora_fine: string;
  note: string | null;
  stato: string;
  ricorrenza_id: string | null;
  utente_nome: string;
  punto_vendita_nome: string | null;
  mansione_nome: string | null;
  mansione_colore: string | null;
  utente_colore: string | null;
  creato_da_nome: string | null;
  pausa_minuti: number;
  pausa_inizio: string | null;
  pausa_fine: string | null;
}

interface TurnoTemplate {
  id: number;
  nome: string;
  ora_inizio: string;
  ora_fine: string;
  colore: string;
}

interface Mansione {
  id: number;
  nome: string;
  colore: string;
  attivo: boolean;
  ordine: number;
}

interface PuntoVendita {
  id: number;
  nome: string;
}

interface Utente {
  id: number;
  nome: string;
  ruolo: string;
  attivo: boolean;
  costo_orario?: number;
}

interface ColoreUtente {
  utente_id: number;
  colore: string;
  utente_nome: string;
}

interface RiepilogoOre {
  utente_id: number;
  utente_nome: string;
  num_turni: number;
  ore_totali: number;
  ore_medie_turno: number;
  ore_per_mansione: Record<string, number> | null;
  costo_orario: number;
  costo_totale: number;
}

interface ScambioTurno {
  id: number;
  turno_originale_id: number;
  richiedente_id: number;
  destinatario_id: number;
  turno_proposto_id: number | null;
  stato: string;
  nota_richiedente: string | null;
  nota_risposta: string | null;
  approvato_da: number | null;
  turno_data: string;
  turno_ora_inizio: string;
  turno_ora_fine: string;
  richiedente_nome: string;
  destinatario_nome: string;
  approvato_da_nome: string | null;
  punto_vendita_nome: string | null;
}

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: Turno;
}

const STATI_TURNO = [
  { value: 'confermato', label: 'Confermato', colore: '#28a745' },
  { value: 'da_confermare', label: 'Da confermare', colore: '#ffc107' },
  { value: 'annullato', label: 'Annullato', colore: '#dc3545' },
  { value: 'malattia', label: 'Malattia', colore: '#fd7e14' },
  { value: 'ferie', label: 'Ferie', colore: '#17a2b8' },
  { value: 'permesso', label: 'Permesso', colore: '#6f42c1' }
];

const GIORNI_SETTIMANA = [
  { value: 1, label: 'Lunedì' },
  { value: 2, label: 'Martedì' },
  { value: 3, label: 'Mercoledì' },
  { value: 4, label: 'Giovedì' },
  { value: 5, label: 'Venerdì' },
  { value: 6, label: 'Sabato' },
  { value: 0, label: 'Domenica' }
];

const TurnisticaPage: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const isAdmin = user?.ruolo === 'admin';
  const isManager = user?.ruolo === 'manager';
  const canEdit = isAdmin;

  // Stato
  const [turni, setTurni] = useState<Turno[]>([]);
  const [template, setTemplate] = useState<TurnoTemplate[]>([]);
  const [mansioni, setMansioni] = useState<Mansione[]>([]);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [utenti, setUtenti] = useState<Utente[]>([]);
  const [coloriUtenti, setColoriUtenti] = useState<ColoreUtente[]>([]);
  const [riepilogoOre, setRiepilogoOre] = useState<RiepilogoOre[]>([]);
  const [scambi, setScambi] = useState<ScambioTurno[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<View>('week');

  // Filtri
  const [filtroUtente, setFiltroUtente] = useState<string>('');
  const [filtroPV, setFiltroPV] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Modali
  const [showTurnoModal, setShowTurnoModal] = useState(false);
  const [showRicorrenteModal, setShowRicorrenteModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showRiepilogoModal, setShowRiepilogoModal] = useState(false);
  const [showScambiModal, setShowScambiModal] = useState(false);
  const [showScambioCreateModal, setShowScambioCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTurnoDetail, setSelectedTurnoDetail] = useState<Turno | null>(null);
  const [editingTurno, setEditingTurno] = useState<Turno | null>(null);
  const [, setSelectedSlot] = useState<{ start: Date; end: Date } | null>(null);

  // Tabella turni admin
  const [tableGroupBy, setTableGroupBy] = useState<'none' | 'utente' | 'giorno'>('none');
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editRowData, setEditRowData] = useState<any>({});

  // Form turno
  const [formTurno, setFormTurno] = useState({
    utente_id: '',
    punto_vendita_id: '',
    mansione_id: '',
    data: '',
    ora_inizio: '',
    ora_fine: '',
    note: '',
    stato: 'confermato',
    pausa_inizio: '',
    pausa_fine: ''
  });

  // Form ricorrente
  const [formRicorrente, setFormRicorrente] = useState({
    utente_id: '',
    punto_vendita_id: '',
    mansione_id: '',
    ora_inizio: '',
    ora_fine: '',
    note: '',
    giorni_settimana: [] as number[],
    data_inizio: '',
    data_fine: '',
    pausa_inizio: '',
    pausa_fine: ''
  });

  // Form scambio
  const [formScambio, setFormScambio] = useState({
    destinatario_id: '',
    nota_richiedente: ''
  });
  const [turnoPerScambio, setTurnoPerScambio] = useState<Turno | null>(null);

  // Config tab
  const [configTab, setConfigTab] = useState<'mansioni' | 'template' | 'colori'>('mansioni');
  const [newMansione, setNewMansione] = useState({ nome: '', colore: '#6c757d' });
  const [newTemplate, setNewTemplate] = useState({ nome: '', ora_inizio: '', ora_fine: '', colore: '#007bff' });

  // Calcola range date in base alla vista
  const getDateRange = useCallback(() => {
    const unit = (view === 'agenda' || view === 'work_week') ? 'month' : view as moment.unitOfTime.StartOf;
    const start = moment(currentDate).startOf(unit).subtract(7, 'days');
    const end = moment(currentDate).endOf(unit).add(7, 'days');
    return {
      data_inizio: start.format('YYYY-MM-DD'),
      data_fine: end.format('YYYY-MM-DD')
    };
  }, [currentDate, view]);

  // API helpers
  const api = (ApiService as any);

  const loadTurni = useCallback(async () => {
    try {
      const { data_inizio, data_fine } = getDateRange();
      let url = `/turni?data_inizio=${data_inizio}&data_fine=${data_fine}`;
      if (filtroUtente) url += `&utente_id=${filtroUtente}`;
      if (filtroPV) url += `&punto_vendita_id=${filtroPV}`;
      const response = await api.api.get(url);
      setTurni(response.data);
    } catch (err: any) {
      console.error('Errore caricamento turni:', err);
      setError('Errore nel caricamento dei turni');
    }
  }, [getDateRange, filtroUtente, filtroPV, api]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [turniRes, templateRes, mansioniRes, pvRes, utentiRes, coloriRes] = await Promise.all([
        api.api.get(`/turni?data_inizio=${getDateRange().data_inizio}&data_fine=${getDateRange().data_fine}`),
        api.api.get('/turni/template'),
        api.api.get('/turni/mansioni'),
        api.api.get('/punti-vendita'),
        api.api.get('/users'),
        api.api.get('/turni/colori-utenti')
      ]);

      setTurni(turniRes.data);
      setTemplate(templateRes.data);
      setMansioni(mansioniRes.data);
      setPuntiVendita(Array.isArray(pvRes.data) ? pvRes.data : pvRes.data?.puntiVendita || []);
      setUtenti(Array.isArray(utentiRes.data) ? utentiRes.data.filter((u: Utente) => u.attivo) : []);
      setColoriUtenti(coloriRes.data);
      setError(null);
    } catch (err: any) {
      console.error('Errore caricamento dati:', err);
      if (err.response?.status === 500 && err.response?.data?.error?.includes('does not exist')) {
        setError('Tabelle turni non ancora create. Esegui la migrazione del database.');
      } else {
        setError('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  }, [getDateRange, api]);

  useEffect(() => {
    loadAll();
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) {
      loadTurni();
    }
  }, [currentDate, view, filtroUtente, filtroPV]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Converti turni in eventi calendario
  const events: CalendarEvent[] = useMemo(() => {
    return turni.map(t => {
      const start = moment(`${t.data} ${t.ora_inizio}`, 'YYYY-MM-DD HH:mm:ss').toDate();
      let end = moment(`${t.data} ${t.ora_fine}`, 'YYYY-MM-DD HH:mm:ss').toDate();
      // Se l'evento va oltre la mezzanotte, clamp a 23:59 per il calendario
      if (end <= start) {
        end = moment(t.data, 'YYYY-MM-DD').hour(23).minute(59).toDate();
      }
      const mansioneTxt = t.mansione_nome ? ` • ${t.mansione_nome}` : '';
      const pvTxt = t.punto_vendita_nome ? ` @ ${t.punto_vendita_nome}` : '';
      return {
        id: t.id,
        title: `${t.utente_nome}${mansioneTxt}${pvTxt}`,
        start,
        end,
        resource: t
      };
    });
  }, [turni]);

  // Mappa colori per utente
  const coloriMap = useMemo(() => {
    const map: Record<number, string> = {};
    coloriUtenti.forEach(c => { map[c.utente_id] = c.colore; });
    return map;
  }, [coloriUtenti]);

  // Stile evento basato su colore dipendente
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const turno = event.resource;
    const bgColor = coloriMap[turno.utente_id] || turno.utente_colore || '#007bff';
    const isAnnullato = turno.stato === 'annullato';
    const isDaConfermare = turno.stato === 'da_confermare';

    return {
      className: (turno.pausa_inizio && turno.pausa_fine) ? 'turno-event-con-pausa' : '',
      style: {
        backgroundColor: isAnnullato ? '#dee2e6' : bgColor,
        borderColor: isAnnullato ? '#adb5bd' : bgColor,
        color: isAnnullato ? '#6c757d' : 'white',
        opacity: isAnnullato ? 0.6 : isDaConfermare ? 0.8 : 1,
        borderLeft: isDaConfermare ? '3px dashed #ffc107' : `3px solid ${bgColor}`,
        borderRadius: '4px',
        fontSize: '0.8rem',
        padding: '2px 4px',
        textDecoration: isAnnullato ? 'line-through' : 'none'
      }
    };
  }, [coloriMap]);

  // Click su evento
  const handleEventClick = useCallback((event: CalendarEvent) => {
    const turno = event.resource;
    if (canEdit) {
      setEditingTurno(turno);
      setFormTurno({
        utente_id: String(turno.utente_id),
        punto_vendita_id: turno.punto_vendita_id ? String(turno.punto_vendita_id) : '',
        mansione_id: turno.mansione_id ? String(turno.mansione_id) : '',
        data: turno.data?.split('T')[0] || turno.data,
        ora_inizio: turno.ora_inizio?.substring(0, 5) || '',
        ora_fine: turno.ora_fine?.substring(0, 5) || '',
        note: turno.note || '',
        stato: turno.stato || 'confermato',
        pausa_inizio: turno.pausa_inizio?.substring(0, 5) || '',
        pausa_fine: turno.pausa_fine?.substring(0, 5) || ''
      });
      setShowTurnoModal(true);
    } else {
      // Non-admin: mostra dettaglio turno (read-only)
      setSelectedTurnoDetail(turno);
      setShowDetailModal(true);
    }
  }, [canEdit]);

  // Click su slot vuoto → crea turno
  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    if (!canEdit) return;
    const startMoment = moment(slotInfo.start);
    const endMoment = moment(slotInfo.end);

    setEditingTurno(null);
    setFormTurno({
      utente_id: '',
      punto_vendita_id: '',
      mansione_id: '',
      data: startMoment.format('YYYY-MM-DD'),
      ora_inizio: startMoment.format('HH:mm'),
      ora_fine: endMoment.format('HH:mm') === '00:00' ? '18:00' : endMoment.format('HH:mm'),
      note: '',
      stato: 'confermato',
      pausa_inizio: '',
      pausa_fine: ''
    });
    setSelectedSlot({ start: slotInfo.start, end: slotInfo.end });
    setShowTurnoModal(true);
  }, [canEdit]);

  // Drag & Drop evento
  // Applica template
  const applyTemplate = (tmpl: TurnoTemplate) => {
    setFormTurno(prev => ({
      ...prev,
      ora_inizio: tmpl.ora_inizio?.substring(0, 5) || '',
      ora_fine: tmpl.ora_fine?.substring(0, 5) || ''
    }));
  };

  // Salva turno (crea o modifica)
  const handleSaveTurno = async () => {
    try {
      if (!formTurno.utente_id || !formTurno.data || !formTurno.ora_inizio || !formTurno.ora_fine) {
        setError('Compila tutti i campi obbligatori');
        return;
      }

      const payload = {
        utente_id: parseInt(formTurno.utente_id),
        punto_vendita_id: formTurno.punto_vendita_id ? parseInt(formTurno.punto_vendita_id) : null,
        mansione_id: formTurno.mansione_id ? parseInt(formTurno.mansione_id) : null,
        data: formTurno.data,
        ora_inizio: formTurno.ora_inizio,
        ora_fine: formTurno.ora_fine,
        note: formTurno.note || null,
        stato: formTurno.stato,
        pausa_inizio: formTurno.pausa_inizio || null,
        pausa_fine: formTurno.pausa_fine || null
      };

      if (editingTurno) {
        await api.api.put(`/turni/${editingTurno.id}`, payload);
      } else {
        await api.api.post('/turni', payload);
      }

      setShowTurnoModal(false);
      setEditingTurno(null);
      loadTurni();
    } catch (err: any) {
      console.error('Errore salvataggio turno:', err);
      setError(err.response?.data?.error || 'Errore nel salvataggio');
    }
  };

  // Elimina turno
  const handleDeleteTurno = async () => {
    if (!editingTurno) return;
    const ok = await confirm({
      title: 'Elimina turno',
      message: 'Eliminare questo turno?',
      variant: 'danger',
      confirmLabel: 'Elimina'
    });
    if (!ok) return;
    try {
      await api.api.delete(`/turni/${editingTurno.id}`);
      setShowTurnoModal(false);
      setEditingTurno(null);
      toast.success('Turno eliminato');
      loadTurni();
    } catch (err) {
      console.error('Errore eliminazione turno:', err);
      setError('Errore nell\'eliminazione del turno');
    }
  };

  // Elimina ricorrenza futura
  const handleDeleteRicorrenza = async () => {
    if (!editingTurno?.ricorrenza_id) return;
    const ok = await confirm({
      title: 'Elimina ricorrenza',
      message: 'Eliminare tutti i turni futuri di questa ricorrenza?',
      variant: 'danger',
      confirmLabel: 'Elimina tutti'
    });
    if (!ok) return;
    try {
      await api.api.delete(`/turni/ricorrenza/${editingTurno.ricorrenza_id}`);
      setShowTurnoModal(false);
      setEditingTurno(null);
      toast.success('Turni della ricorrenza eliminati');
      loadTurni();
    } catch (err) {
      console.error('Errore eliminazione ricorrenza:', err);
      setError('Errore nell\'eliminazione della ricorrenza');
    }
  };

  // Cancella tutti i turni (con doppia conferma, la seconda con testo obbligatorio)
  const handleDeleteAllTurni = async () => {
    if (!isAdmin) return;

    const firstOk = await confirm({
      title: 'Cancella tutti i turni',
      message: 'Vuoi cancellare tutti i turni presenti nel sistema?',
      variant: 'danger',
      confirmLabel: 'Continua'
    });
    if (!firstOk) return;

    const secondOk = await confirm({
      title: 'Conferma definitiva',
      message: 'Questa operazione eliminerà permanentemente tutti i turni di tutti i dipendenti dal database.',
      variant: 'danger',
      confirmLabel: 'Cancella tutto',
      requireText: 'CANCELLA'
    });
    if (!secondOk) return;

    try {
      setLoading(true);
      const response = await api.api.delete('/turni/cancella-tutti');
      toast.success(`Eliminati ${response.data.count} turni`);
      loadTurni();
    } catch (err: any) {
      console.error('Errore durante la cancellazione di tutti i turni:', err);
      setError(err.response?.data?.error || 'Errore durante la cancellazione di tutti i turni');
    } finally {
      setLoading(false);
    }
  };

  // Salva turni ricorrenti
  const handleSaveRicorrente = async () => {
    try {
      if (!formRicorrente.utente_id || !formRicorrente.ora_inizio || !formRicorrente.ora_fine ||
          !formRicorrente.data_inizio || !formRicorrente.data_fine || formRicorrente.giorni_settimana.length === 0) {
        setError('Compila tutti i campi obbligatori');
        return;
      }

      await api.api.post('/turni/ricorrenti', {
        utente_id: parseInt(formRicorrente.utente_id),
        punto_vendita_id: formRicorrente.punto_vendita_id ? parseInt(formRicorrente.punto_vendita_id) : null,
        mansione_id: formRicorrente.mansione_id ? parseInt(formRicorrente.mansione_id) : null,
        ora_inizio: formRicorrente.ora_inizio,
        ora_fine: formRicorrente.ora_fine,
        note: formRicorrente.note || null,
        giorni_settimana: formRicorrente.giorni_settimana,
        data_inizio: formRicorrente.data_inizio,
        data_fine: formRicorrente.data_fine,
        pausa_inizio: formRicorrente.pausa_inizio || null,
        pausa_fine: formRicorrente.pausa_fine || null
      });

      setShowRicorrenteModal(false);
      setFormRicorrente({
        utente_id: '', punto_vendita_id: '', mansione_id: '',
        ora_inizio: '', ora_fine: '', note: '',
        giorni_settimana: [], data_inizio: '', data_fine: '',
        pausa_inizio: '', pausa_fine: ''
      });
      loadTurni();
    } catch (err: any) {
      console.error('Errore turni ricorrenti:', err);
      setError(err.response?.data?.error || 'Errore nella creazione dei turni ricorrenti');
    }
  };

  // Carica riepilogo ore
  const loadRiepilogoOre = async () => {
    try {
      const startOfMonth = moment(currentDate).startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment(currentDate).endOf('month').format('YYYY-MM-DD');
      const response = await api.api.get(`/turni/riepilogo-ore?data_inizio=${startOfMonth}&data_fine=${endOfMonth}`);
      setRiepilogoOre(response.data);
      setShowRiepilogoModal(true);
    } catch (err) {
      console.error('Errore riepilogo ore:', err);
      setError('Errore nel caricamento del riepilogo');
    }
  };

  // Carica scambi
  const loadScambi = async () => {
    try {
      const response = await api.api.get('/turni/scambi');
      setScambi(response.data);
      setShowScambiModal(true);
    } catch (err) {
      console.error('Errore caricamento scambi:', err);
    }
  };

  // Init defaults
  const handleInitDefaults = async () => {
    try {
      await api.api.post('/turni/init-defaults');
      loadAll();
    } catch (err) {
      console.error('Errore init defaults:', err);
    }
  };

  // Config: salva mansione
  const handleSaveMansione = async () => {
    if (!newMansione.nome) return;
    try {
      await api.api.post('/turni/mansioni', newMansione);
      setNewMansione({ nome: '', colore: '#6c757d' });
      const res = await api.api.get('/turni/mansioni');
      setMansioni(res.data);
    } catch (err) {
      console.error('Errore salvataggio mansione:', err);
    }
  };

  const handleDeleteMansione = async (id: number) => {
    const ok = await confirm({
      title: 'Elimina mansione',
      message: 'Eliminare questa mansione?',
      variant: 'danger',
      confirmLabel: 'Elimina'
    });
    if (!ok) return;
    try {
      await api.api.delete(`/turni/mansioni/${id}`);
      const res = await api.api.get('/turni/mansioni');
      setMansioni(res.data);
    } catch (err) {
      console.error('Errore eliminazione mansione:', err);
    }
  };

  // Config: salva template
  const handleSaveTemplate = async () => {
    if (!newTemplate.nome || !newTemplate.ora_inizio || !newTemplate.ora_fine) return;
    try {
      await api.api.post('/turni/template', newTemplate);
      setNewTemplate({ nome: '', ora_inizio: '', ora_fine: '', colore: '#007bff' });
      const res = await api.api.get('/turni/template');
      setTemplate(res.data);
    } catch (err) {
      console.error('Errore salvataggio template:', err);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    const ok = await confirm({
      title: 'Elimina template',
      message: 'Eliminare questo template?',
      variant: 'danger',
      confirmLabel: 'Elimina'
    });
    if (!ok) return;
    try {
      await api.api.delete(`/turni/template/${id}`);
      const res = await api.api.get('/turni/template');
      setTemplate(res.data);
    } catch (err) {
      console.error('Errore eliminazione template:', err);
    }
  };

  // Config: salva colore utente
  const handleSaveColoreUtente = async (utenteId: number, colore: string) => {
    try {
      await api.api.put(`/turni/colori-utenti/${utenteId}`, { colore });
      const res = await api.api.get('/turni/colori-utenti');
      setColoriUtenti(res.data);
    } catch (err) {
      console.error('Errore salvataggio colore:', err);
    }
  };

  // Scambio turno
  const handleProponiScambio = (turno: Turno) => {
    setTurnoPerScambio(turno);
    setFormScambio({ destinatario_id: '', nota_richiedente: '' });
    setShowScambioCreateModal(true);
  };

  const handleSaveScambio = async () => {
    if (!turnoPerScambio || !formScambio.destinatario_id) return;
    try {
      await api.api.post('/turni/scambi', {
        turno_originale_id: turnoPerScambio.id,
        destinatario_id: parseInt(formScambio.destinatario_id),
        nota_richiedente: formScambio.nota_richiedente || null
      });
      setShowScambioCreateModal(false);
      setTurnoPerScambio(null);
      loadScambi();
    } catch (err: any) {
      console.error('Errore proposta scambio:', err);
      setError(err.response?.data?.error || 'Errore nella proposta di scambio');
    }
  };

  const handleRispondiScambio = async (scambioId: number, accettato: boolean) => {
    try {
      await api.api.put(`/turni/scambi/${scambioId}/rispondi`, { accettato });
      loadScambi();
    } catch (err) {
      console.error('Errore risposta scambio:', err);
    }
  };

  const handleApprovaScambio = async (scambioId: number, approvato: boolean) => {
    try {
      await api.api.put(`/turni/scambi/${scambioId}/approva`, { approvato });
      loadScambi();
      loadTurni();
    } catch (err) {
      console.error('Errore approvazione scambio:', err);
    }
  };

  // Nomi giorni settimana
  const GIORNI_NOMI = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

  // Componente evento custom — semplificato per compatibilità RBC
  const EventComponent = ({ event }: { event: CalendarEvent }) => {
    const t = event.resource;
    const hasPausa = t.pausa_inizio && t.pausa_fine;

    // Vista mese: compatta
    if (view === 'month') {
      return (
        <div className="turno-event turno-event-month">
          <span className="turno-event-name">{t.utente_nome}</span>
          <span className="turno-event-orario">{t.ora_inizio?.substring(0,5)}-{t.ora_fine?.substring(0,5)}</span>
        </div>
      );
    }

    // Vista agenda: inline
    if (view === 'agenda') {
      return (
        <span className="turno-event-agenda">
          <strong>{t.utente_nome}</strong>
          {t.mansione_nome && <span> • {t.mansione_nome}</span>}
          {hasPausa && <span className="turno-agenda-pausa"> • Pausa {t.pausa_inizio!.substring(0,5)}-{t.pausa_fine!.substring(0,5)}</span>}
          {t.punto_vendita_nome && <span> @ {t.punto_vendita_nome}</span>}
        </span>
      );
    }

    // Vista settimana/giorno: SOLO nome + mansione (l'orario lo mostra RBC nativamente)
    return (
      <div className="turno-event turno-event-week">
        <strong>{t.utente_nome}</strong>
        {t.mansione_nome && <div className="turno-event-mansione">{t.mansione_nome}</div>}
        {hasPausa && <div className="turno-event-pausa-label">Pausa {t.pausa_inizio!.substring(0,5)}-{t.pausa_fine!.substring(0,5)}</div>}
      </div>
    );
  };

  // Legenda colori utenti
  const legendaUtenti = useMemo(() => {
    const utentiConTurni = Array.from(new Set(turni.map(t => t.utente_id)));
    return utentiConTurni.map(uid => {
      const turno = turni.find(t => t.utente_id === uid);
      return {
        id: uid,
        nome: turno?.utente_nome || '',
        colore: coloriMap[uid] || turno?.utente_colore || '#007bff'
      };
    });
  }, [turni, coloriMap]);

  if (loading) {
    return (
      <div className="turnistica-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Caricamento turni...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="turnistica-page">
      {/* Header */}
      <div className="turnistica-header">
        <div className="header-title">
          <CalendarIcon size={28} />
          <div>
            <h1>Turnistica</h1>
            <p>Gestione turni dipendenti</p>
          </div>
        </div>
        <div className="header-actions">
          {canEdit && (
            <>
              <button className="btn-turno-action btn-primary" onClick={() => {
                setEditingTurno(null);
                setFormTurno({ utente_id: '', punto_vendita_id: '', mansione_id: '', data: moment().format('YYYY-MM-DD'), ora_inizio: '08:00', ora_fine: '16:00', note: '', stato: 'confermato', pausa_inizio: '', pausa_fine: '' });
                setShowTurnoModal(true);
              }}>
                <Plus size={16} /> Nuovo Turno
              </button>
              <button className="btn-turno-action btn-secondary" onClick={() => {
                setFormRicorrente({ utente_id: '', punto_vendita_id: '', mansione_id: '', ora_inizio: '08:00', ora_fine: '16:00', note: '', giorni_settimana: [], data_inizio: moment().format('YYYY-MM-DD'), data_fine: moment().add(4, 'weeks').format('YYYY-MM-DD'), pausa_inizio: '', pausa_fine: '' });
                setShowRicorrenteModal(true);
              }}>
                <RefreshCw size={16} /> Ricorrente
              </button>
              {isAdmin && (
                <button className="btn-turno-action btn-danger" onClick={handleDeleteAllTurni}>
                  <Trash2 size={16} /> Cancella Tutto
                </button>
              )}
            </>
          )}
          <button className="btn-turno-action btn-outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} /> Filtri
          </button>
          {(isAdmin || isManager) && (
            <button className="btn-turno-action btn-outline" onClick={loadRiepilogoOre}>
              <BarChart3 size={16} /> Ore
            </button>
          )}
          <button className="btn-turno-action btn-outline" onClick={loadScambi}>
            <ArrowLeftRight size={16} /> Scambi
          </button>
          {isAdmin && (
            <button className="btn-turno-action btn-outline" onClick={() => setShowConfigModal(true)}>
              <Settings size={16} /> Config
            </button>
          )}
        </div>
      </div>

      {/* Errore */}
      {error && (
        <div className="turnistica-error">
          <span><AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />{error}</span>
          <button onClick={() => setError(null)}>✕</button>
          {error.includes('migrazione') && isAdmin && (
            <button className="btn-turno-action btn-primary" style={{ marginLeft: 8 }} onClick={handleInitDefaults}>
              Inizializza Tabelle
            </button>
          )}
        </div>
      )}

      {/* Filtri */}
      {showFilters && (
        <div className="turnistica-filters">
          <div className="filter-group">
            <label>Dipendente</label>
            <select value={filtroUtente} onChange={e => setFiltroUtente(e.target.value)}>
              <option value="">Tutti</option>
              {utenti.map(u => (
                <option key={u.id} value={u.id}>{u.nome}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Punto Vendita</label>
            <select value={filtroPV} onChange={e => setFiltroPV(e.target.value)}>
              <option value="">Tutti</option>
              {puntiVendita.map(pv => (
                <option key={pv.id} value={pv.id}>{pv.nome}</option>
              ))}
            </select>
          </div>
          <button className="btn-turno-action btn-outline" onClick={() => { setFiltroUtente(''); setFiltroPV(''); }}>
            Reset
          </button>
        </div>
      )}

      {/* Legenda colori */}
      {legendaUtenti.length > 0 && (
        <div className="turnistica-legenda">
          {legendaUtenti.map(u => (
            <div key={u.id} className="legenda-item">
              <span className="legenda-dot" style={{ backgroundColor: u.colore }}></span>
              <span>{u.nome}</span>
            </div>
          ))}
        </div>
      )}

      {/* Calendario */}
      <div className="turnistica-calendar-container">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 750 }}
          view={view}
          views={['month', 'week', 'day', 'agenda']}
          onView={(v) => setView(v)}
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          messages={messages}
          onSelectEvent={handleEventClick}
          onSelectSlot={handleSelectSlot}
          selectable={canEdit}
          eventPropGetter={eventStyleGetter}
          components={{ event: EventComponent }}
          popup
          showAllEvents
          step={30}
          timeslots={2}
          min={new Date(2026, 0, 1, 5, 0)}
          max={new Date(2026, 0, 1, 23, 59)}
          tooltipAccessor={(event: CalendarEvent) => {
            const t = event.resource;
            const p = t.pausa_inizio && t.pausa_fine ? ` | Pausa: ${t.pausa_inizio.substring(0,5)}-${t.pausa_fine.substring(0,5)}` : '';
            return `${t.utente_nome} ${t.ora_inizio?.substring(0,5)}-${t.ora_fine?.substring(0,5)}${p}${t.mansione_nome ? ' • ' + t.mansione_nome : ''}`;
          }}
        />
      </div>

      {/* ============== TABELLA TURNI ADMIN ============== */}
      {canEdit && turni.length > 0 && (() => {
        // Ordina turni
        const sorted = [...turni].sort((a, b) => {
          const dCmp = (a.data || '').localeCompare(b.data || '');
          if (dCmp !== 0) return dCmp;
          return (a.ora_inizio || '').localeCompare(b.ora_inizio || '');
        });

        // Raggruppa
        const groups: { label: string; items: Turno[] }[] = [];
        if (tableGroupBy === 'utente') {
          const byUser: Record<string, Turno[]> = {};
          for (const t of sorted) {
            const key = t.utente_nome || 'Sconosciuto';
            if (!byUser[key]) byUser[key] = [];
            byUser[key].push(t);
          }
          for (const [name, items] of Object.entries(byUser)) {
            groups.push({ label: name, items });
          }
        } else if (tableGroupBy === 'giorno') {
          const byDay: Record<string, Turno[]> = {};
          for (const t of sorted) {
            const d = t.data?.split('T')[0] || '';
            const dayName = d ? GIORNI_NOMI[new Date(d + 'T12:00:00').getDay()] : '';
            const parts = d.split('-');
            const key = d ? `${dayName} ${parts[2]}/${parts[1]}` : 'Senza data';
            if (!byDay[key]) byDay[key] = [];
            byDay[key].push(t);
          }
          for (const [day, items] of Object.entries(byDay)) {
            groups.push({ label: day, items });
          }
        } else {
          groups.push({ label: '', items: sorted });
        }

        // Funzione salva inline
        const handleInlineSave = async (turnoId: number) => {
          try {
            await api.api.put(`/turni/${turnoId}`, {
              utente_id: parseInt(editRowData.utente_id),
              punto_vendita_id: editRowData.punto_vendita_id ? parseInt(editRowData.punto_vendita_id) : null,
              mansione_id: editRowData.mansione_id ? parseInt(editRowData.mansione_id) : null,
              data: editRowData.data,
              ora_inizio: editRowData.ora_inizio,
              ora_fine: editRowData.ora_fine,
              note: editRowData.note || null,
              stato: editRowData.stato,
              pausa_inizio: editRowData.pausa_inizio || null,
              pausa_fine: editRowData.pausa_fine || null
            });
            setEditingRowId(null);
            loadTurni();
          } catch (err: any) {
            setError(err.response?.data?.error || 'Errore salvataggio');
          }
        };

        const startEdit = (t: Turno) => {
          setEditingRowId(t.id);
          setEditRowData({
            utente_id: String(t.utente_id),
            punto_vendita_id: t.punto_vendita_id ? String(t.punto_vendita_id) : '',
            mansione_id: t.mansione_id ? String(t.mansione_id) : '',
            data: t.data?.split('T')[0] || t.data,
            ora_inizio: t.ora_inizio?.substring(0, 5) || '',
            ora_fine: t.ora_fine?.substring(0, 5) || '',
            note: t.note || '',
            stato: t.stato || 'confermato',
            pausa_inizio: t.pausa_inizio?.substring(0, 5) || '',
            pausa_fine: t.pausa_fine?.substring(0, 5) || ''
          });
        };

        // Render riga (editing o read-only)
        const renderRow = (t: Turno) => {
          const isEditing = editingRowId === t.id;
          const si = STATI_TURNO.find(s => s.value === t.stato);
          const uColor = coloriMap[t.utente_id] || t.utente_colore || '#007bff';
          const d = t.data?.split('T')[0] || '';
          const dayName = d ? GIORNI_NOMI[new Date(d + 'T12:00:00').getDay()] : '';
          const parts = d.split('-');
          const dataFormatted = d ? `${parts[2]}/${parts[1]}` : '-';

          if (isEditing) {
            return (
              <tr key={t.id} className="editing-row">
                <td>{dayName}</td>
                <td><input type="date" value={editRowData.data} onChange={e => setEditRowData({ ...editRowData, data: e.target.value })} className="td-input" /></td>
                <td>
                  <select value={editRowData.utente_id} onChange={e => setEditRowData({ ...editRowData, utente_id: e.target.value })} className="td-select">
                    {utenti.filter(u => u.attivo).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                  </select>
                </td>
                <td><input type="time" value={editRowData.ora_inizio} onChange={e => setEditRowData({ ...editRowData, ora_inizio: e.target.value })} className="td-input" /></td>
                <td><input type="time" value={editRowData.ora_fine} onChange={e => setEditRowData({ ...editRowData, ora_fine: e.target.value })} className="td-input" /></td>
                <td>
                  <input type="time" value={editRowData.pausa_inizio} onChange={e => setEditRowData({ ...editRowData, pausa_inizio: e.target.value })} className="td-input td-input-sm" placeholder="da" />
                  <input type="time" value={editRowData.pausa_fine} onChange={e => setEditRowData({ ...editRowData, pausa_fine: e.target.value })} className="td-input td-input-sm" placeholder="a" />
                </td>
                <td>
                  <select value={editRowData.mansione_id} onChange={e => setEditRowData({ ...editRowData, mansione_id: e.target.value })} className="td-select">
                    <option value="">-</option>
                    {mansioni.filter(m => m.attivo).map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                  </select>
                </td>
                <td>
                  <select value={editRowData.stato} onChange={e => setEditRowData({ ...editRowData, stato: e.target.value })} className="td-select">
                    {STATI_TURNO.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td><input type="text" value={editRowData.note} onChange={e => setEditRowData({ ...editRowData, note: e.target.value })} className="td-input" placeholder="Note..." /></td>
                <td className="td-actions">
                  <button className="btn-save" onClick={() => handleInlineSave(t.id)}><Check size={13} /> Salva</button>
                  <button className="btn-cancel" onClick={() => setEditingRowId(null)}><X size={13} /></button>
                </td>
              </tr>
            );
          }

          return (
            <tr key={t.id}>
              <td>{dayName}</td>
              <td>{dataFormatted}</td>
              <td className="td-nome">
                <span className="td-color-dot" style={{ backgroundColor: uColor }} />
                {t.utente_nome}
              </td>
              <td className="td-orario">{t.ora_inizio?.substring(0, 5)} - {t.ora_fine?.substring(0, 5)}</td>
              <td className="td-orario">{(() => {
                if (!t.ora_inizio || !t.ora_fine) return '-';
                const [hi, mi] = t.ora_inizio.split(':').map(Number);
                const [hf, mf] = t.ora_fine.split(':').map(Number);
                let diff = (hf * 60 + mf) - (hi * 60 + mi);
                if (diff < 0) diff += 24 * 60;
                if (t.pausa_inizio && t.pausa_fine) {
                  const [pi, pmi] = t.pausa_inizio.split(':').map(Number);
                  const [pf, pmf] = t.pausa_fine.split(':').map(Number);
                  diff -= ((pf * 60 + pmf) - (pi * 60 + pmi));
                }
                const h = Math.floor(diff / 60);
                const m = diff % 60;
                return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
              })()}</td>
              <td className="td-pausa">
                {t.pausa_inizio && t.pausa_fine
                  ? `${t.pausa_inizio.substring(0, 5)}-${t.pausa_fine.substring(0, 5)}`
                  : '-'}
              </td>
              <td>{t.mansione_nome || '-'}</td>
              <td className="td-stato">
                {si ? <span style={{ color: si.colore }}>● {si.label}</span> : t.stato}
              </td>
              <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.note || '-'}</td>
              <td className="td-actions">
                <button className="btn-edit" title="Modifica inline" onClick={() => startEdit(t)}>
                  <Edit size={13} /> Mod
                </button>
                <button
                  className="btn-delete"
                  title="Elimina"
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Elimina turno',
                      message: `Eliminare il turno di ${t.utente_nome} del ${dataFormatted}?`,
                      variant: 'danger',
                      confirmLabel: 'Elimina'
                    });
                    if (!ok) return;
                    try {
                      await api.api.delete(`/turni/${t.id}`);
                      toast.success('Turno eliminato');
                      loadTurni();
                    } catch (err) {
                      setError('Errore eliminazione turno');
                    }
                  }}
                >
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          );
        };

        return (
          <div className="turni-table-section">
            <div className="turni-table-header">
              <h3>Gestione Turni ({turni.length})</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '0.8rem', color: '#868e96' }}>Raggruppa:</label>
                <select
                  value={tableGroupBy}
                  onChange={e => setTableGroupBy(e.target.value as any)}
                  style={{ fontSize: '0.8rem', padding: '4px 8px', borderRadius: '6px', border: '1px solid #dee2e6' }}
                >
                  <option value="none">Nessuno</option>
                  <option value="utente">Per Dipendente</option>
                  <option value="giorno">Per Giorno</option>
                </select>
              </div>
            </div>
            <div className="turni-table-wrapper">
              <table className="turni-table">
                <thead>
                  <tr>
                    <th>Giorno</th>
                    <th>Data</th>
                    <th>Dipendente</th>
                    <th>Orario</th>
                    <th>Ore</th>
                    <th>Pausa</th>
                    <th>Mansione</th>
                    <th>Stato</th>
                    <th>Note</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group, gi) => (
                    <React.Fragment key={gi}>
                      {group.label && (
                        <tr className="table-group-header">
                          <td colSpan={10}>
                            <strong>{group.label}</strong>
                            <span className="group-count">{group.items.length} turni</span>
                          </td>
                        </tr>
                      )}
                      {group.items.map(t => renderRow(t))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ============== MODAL: Crea/Modifica Turno ============== */}
      {showTurnoModal && (
        <div className="turno-modal-overlay" onClick={() => setShowTurnoModal(false)}>
          <div className="turno-modal" onClick={e => e.stopPropagation()}>
            <div className="turno-modal-header">
              <h2>{editingTurno ? 'Modifica Turno' : 'Nuovo Turno'}</h2>
              <button className="modal-close" onClick={() => setShowTurnoModal(false)}><X size={20} /></button>
            </div>
            <div className="turno-modal-body">
              {/* Template rapidi */}
              {template.length > 0 && !editingTurno && (
                <div className="template-quick">
                  <label>Template rapidi:</label>
                  <div className="template-buttons">
                    {template.map(t => (
                      <button key={t.id} className="btn-template" style={{ borderColor: t.colore, color: t.colore }} onClick={() => applyTemplate(t)}>
                        {t.nome} ({t.ora_inizio?.substring(0,5)}-{t.ora_fine?.substring(0,5)})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Dipendente *</label>
                  <select value={formTurno.utente_id} onChange={e => setFormTurno({ ...formTurno, utente_id: e.target.value })}>
                    <option value="">Seleziona...</option>
                    {utenti.map(u => (
                      <option key={u.id} value={u.id}>{u.nome} ({u.ruolo})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Data *</label>
                  <input type="date" value={formTurno.data} onChange={e => setFormTurno({ ...formTurno, data: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Ora Inizio *</label>
                  <input type="time" value={formTurno.ora_inizio} onChange={e => setFormTurno({ ...formTurno, ora_inizio: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Ora Fine *</label>
                  <input type="time" value={formTurno.ora_fine} onChange={e => setFormTurno({ ...formTurno, ora_fine: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Punto Vendita</label>
                  <select value={formTurno.punto_vendita_id} onChange={e => setFormTurno({ ...formTurno, punto_vendita_id: e.target.value })}>
                    <option value="">Nessuno</option>
                    {puntiVendita.map(pv => (
                      <option key={pv.id} value={pv.id}>{pv.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mansione</label>
                  <select value={formTurno.mansione_id} onChange={e => setFormTurno({ ...formTurno, mansione_id: e.target.value })}>
                    <option value="">Nessuna</option>
                    {mansioni.filter(m => m.attivo).map(m => (
                      <option key={m.id} value={m.id}>{m.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stato</label>
                  <select value={formTurno.stato} onChange={e => setFormTurno({ ...formTurno, stato: e.target.value })}>
                    {STATI_TURNO.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Pausa dalle</label>
                  <input type="time" value={formTurno.pausa_inizio} onChange={e => setFormTurno({ ...formTurno, pausa_inizio: e.target.value })} placeholder="Es. 12:00" />
                </div>
                <div className="form-group">
                  <label>Pausa alle</label>
                  <input type="time" value={formTurno.pausa_fine} onChange={e => setFormTurno({ ...formTurno, pausa_fine: e.target.value })} placeholder="Es. 13:00" />
                </div>
                <div className="form-group form-full">
                  <label>Note</label>
                  <textarea value={formTurno.note} onChange={e => setFormTurno({ ...formTurno, note: e.target.value })} rows={2} placeholder="Note opzionali..." />
                </div>
              </div>
            </div>
            <div className="turno-modal-footer">
              {editingTurno && (
                <div className="footer-left">
                  <button className="btn-turno-action btn-danger" onClick={handleDeleteTurno}>
                    <Trash2 size={16} /> Elimina
                  </button>
                  {editingTurno.ricorrenza_id && (
                    <button className="btn-turno-action btn-danger-outline" onClick={handleDeleteRicorrenza}>
                      Elimina ricorrenza
                    </button>
                  )}
                  {/* Pulsante scambio per tutti */}
                  {editingTurno.utente_id === user?.id && (
                    <button className="btn-turno-action btn-outline" onClick={() => { setShowTurnoModal(false); handleProponiScambio(editingTurno); }}>
                      <ArrowLeftRight size={16} /> Proponi scambio
                    </button>
                  )}
                </div>
              )}
              <div className="footer-right">
                <button className="btn-turno-action btn-outline" onClick={() => setShowTurnoModal(false)}>Annulla</button>
                <button className="btn-turno-action btn-primary" onClick={handleSaveTurno}>
                  <Save size={16} /> {editingTurno ? 'Salva' : 'Crea'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== MODAL: Turni Ricorrenti ============== */}
      {showRicorrenteModal && (
        <div className="turno-modal-overlay" onClick={() => setShowRicorrenteModal(false)}>
          <div className="turno-modal" onClick={e => e.stopPropagation()}>
            <div className="turno-modal-header">
              <h2>Turni Ricorrenti</h2>
              <button className="modal-close" onClick={() => setShowRicorrenteModal(false)}><X size={20} /></button>
            </div>
            <div className="turno-modal-body">
              {template.length > 0 && (
                <div className="template-quick">
                  <label>Template rapidi:</label>
                  <div className="template-buttons">
                    {template.map(t => (
                      <button key={t.id} className="btn-template" style={{ borderColor: t.colore, color: t.colore }} onClick={() => setFormRicorrente(prev => ({ ...prev, ora_inizio: t.ora_inizio?.substring(0,5) || '', ora_fine: t.ora_fine?.substring(0,5) || '' }))}>
                        {t.nome} ({t.ora_inizio?.substring(0,5)}-{t.ora_fine?.substring(0,5)})
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Dipendente *</label>
                  <select value={formRicorrente.utente_id} onChange={e => setFormRicorrente({ ...formRicorrente, utente_id: e.target.value })}>
                    <option value="">Seleziona...</option>
                    {utenti.map(u => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Punto Vendita</label>
                  <select value={formRicorrente.punto_vendita_id} onChange={e => setFormRicorrente({ ...formRicorrente, punto_vendita_id: e.target.value })}>
                    <option value="">Nessuno</option>
                    {puntiVendita.map(pv => (
                      <option key={pv.id} value={pv.id}>{pv.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Ora Inizio *</label>
                  <input type="time" value={formRicorrente.ora_inizio} onChange={e => setFormRicorrente({ ...formRicorrente, ora_inizio: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Ora Fine *</label>
                  <input type="time" value={formRicorrente.ora_fine} onChange={e => setFormRicorrente({ ...formRicorrente, ora_fine: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Mansione</label>
                  <select value={formRicorrente.mansione_id} onChange={e => setFormRicorrente({ ...formRicorrente, mansione_id: e.target.value })}>
                    <option value="">Nessuna</option>
                    {mansioni.filter(m => m.attivo).map(m => (
                      <option key={m.id} value={m.id}>{m.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Dal *</label>
                  <input type="date" value={formRicorrente.data_inizio} onChange={e => setFormRicorrente({ ...formRicorrente, data_inizio: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Al *</label>
                  <input type="date" value={formRicorrente.data_fine} onChange={e => setFormRicorrente({ ...formRicorrente, data_fine: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Pausa dalle</label>
                  <input type="time" value={formRicorrente.pausa_inizio} onChange={e => setFormRicorrente({ ...formRicorrente, pausa_inizio: e.target.value })} placeholder="Es. 12:00" />
                </div>
                <div className="form-group">
                  <label>Pausa alle</label>
                  <input type="time" value={formRicorrente.pausa_fine} onChange={e => setFormRicorrente({ ...formRicorrente, pausa_fine: e.target.value })} placeholder="Es. 13:00" />
                </div>
              </div>

              <div className="form-group form-full" style={{ marginTop: '1rem' }}>
                <label>Giorni della settimana *</label>
                <div className="giorni-checkboxes">
                  {GIORNI_SETTIMANA.map(g => (
                    <label key={g.value} className={`giorno-check ${formRicorrente.giorni_settimana.includes(g.value) ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={formRicorrente.giorni_settimana.includes(g.value)}
                        onChange={e => {
                          const giorni = e.target.checked
                            ? [...formRicorrente.giorni_settimana, g.value]
                            : formRicorrente.giorni_settimana.filter(d => d !== g.value);
                          setFormRicorrente({ ...formRicorrente, giorni_settimana: giorni });
                        }}
                      />
                      {g.label.substring(0, 3)}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group form-full">
                <label>Note</label>
                <textarea value={formRicorrente.note} onChange={e => setFormRicorrente({ ...formRicorrente, note: e.target.value })} rows={2} placeholder="Note opzionali..." />
              </div>
            </div>
            <div className="turno-modal-footer">
              <div className="footer-right">
                <button className="btn-turno-action btn-outline" onClick={() => setShowRicorrenteModal(false)}>Annulla</button>
                <button className="btn-turno-action btn-primary" onClick={handleSaveRicorrente}>
                  <RefreshCw size={16} /> Crea Turni
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== MODAL: Configurazione ============== */}
      {showConfigModal && (
        <div className="turno-modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="turno-modal turno-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="turno-modal-header">
              <h2>Configurazione Turni</h2>
              <button className="modal-close" onClick={() => setShowConfigModal(false)}><X size={20} /></button>
            </div>
            <div className="turno-modal-body">
              {/* Tab */}
              <div className="config-tabs">
                <button className={configTab === 'mansioni' ? 'active' : ''} onClick={() => setConfigTab('mansioni')}>Mansioni</button>
                <button className={configTab === 'template' ? 'active' : ''} onClick={() => setConfigTab('template')}>Template Orari</button>
                <button className={configTab === 'colori' ? 'active' : ''} onClick={() => setConfigTab('colori')}>Colori Dipendenti</button>
              </div>

              {/* Tab Mansioni */}
              {configTab === 'mansioni' && (
                <div className="config-section">
                  <div className="config-add-row">
                    <input type="text" placeholder="Nome mansione" value={newMansione.nome} onChange={e => setNewMansione({ ...newMansione, nome: e.target.value })} />
                    <input type="color" value={newMansione.colore} onChange={e => setNewMansione({ ...newMansione, colore: e.target.value })} />
                    <button className="btn-turno-action btn-primary" onClick={handleSaveMansione}><Plus size={16} /></button>
                  </div>
                  {mansioni.length === 0 && (
                    <div className="config-empty">
                      <p>Nessuna mansione configurata.</p>
                      <button className="btn-turno-action btn-primary" onClick={handleInitDefaults}>Crea defaults</button>
                    </div>
                  )}
                  <div className="config-list">
                    {mansioni.map(m => (
                      <div key={m.id} className="config-item">
                        <span className="config-dot" style={{ backgroundColor: m.colore }}></span>
                        <span className="config-name">{m.nome}</span>
                        <button className="btn-icon-danger" onClick={() => handleDeleteMansione(m.id)}><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab Template */}
              {configTab === 'template' && (
                <div className="config-section">
                  <div className="config-add-row">
                    <input type="text" placeholder="Nome" value={newTemplate.nome} onChange={e => setNewTemplate({ ...newTemplate, nome: e.target.value })} style={{ flex: 2 }} />
                    <input type="time" value={newTemplate.ora_inizio} onChange={e => setNewTemplate({ ...newTemplate, ora_inizio: e.target.value })} />
                    <input type="time" value={newTemplate.ora_fine} onChange={e => setNewTemplate({ ...newTemplate, ora_fine: e.target.value })} />
                    <input type="color" value={newTemplate.colore} onChange={e => setNewTemplate({ ...newTemplate, colore: e.target.value })} />
                    <button className="btn-turno-action btn-primary" onClick={handleSaveTemplate}><Plus size={16} /></button>
                  </div>
                  <div className="config-list">
                    {template.map(t => (
                      <div key={t.id} className="config-item">
                        <span className="config-dot" style={{ backgroundColor: t.colore }}></span>
                        <span className="config-name">{t.nome}</span>
                        <span className="config-detail">{t.ora_inizio?.substring(0,5)} - {t.ora_fine?.substring(0,5)}</span>
                        <button className="btn-icon-danger" onClick={() => handleDeleteTemplate(t.id)}><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab Colori */}
              {configTab === 'colori' && (
                <div className="config-section">
                  <div className="config-list">
                    {utenti.map(u => {
                      const currentColor = coloriMap[u.id] || '#007bff';
                      return (
                        <div key={u.id} className="config-item">
                          <input
                            type="color"
                            value={currentColor}
                            onChange={e => handleSaveColoreUtente(u.id, e.target.value)}
                            style={{ width: 32, height: 32, border: 'none', cursor: 'pointer' }}
                          />
                          <span className="config-name">{u.nome}</span>
                          <span className="config-detail">{u.ruolo}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="turno-modal-footer">
              <div className="footer-right">
                <button className="btn-turno-action btn-outline" onClick={() => setShowConfigModal(false)}>Chiudi</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== MODAL: Riepilogo Ore ============== */}
      {showRiepilogoModal && (
        <div className="turno-modal-overlay" onClick={() => setShowRiepilogoModal(false)}>
          <div className="turno-modal turno-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="turno-modal-header">
              <h2>Riepilogo Ore - {moment(currentDate).format('MMMM YYYY')}</h2>
              <button className="modal-close" onClick={() => setShowRiepilogoModal(false)}><X size={20} /></button>
            </div>
            <div className="turno-modal-body">
              {riepilogoOre.length === 0 ? (
                <p className="no-data">Nessun turno nel periodo selezionato</p>
              ) : (
                <table className="riepilogo-table">
                  <thead>
                    <tr>
                      <th>Dipendente</th>
                      <th>Turni</th>
                      <th>Ore Totali</th>
                      <th>Media/Turno</th>
                      {isAdmin && <th>Costo Orario</th>}
                      {isAdmin && <th>Costo Totale</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {riepilogoOre.map(r => (
                      <tr key={r.utente_id}>
                        <td>
                          <span className="legenda-dot" style={{ backgroundColor: coloriMap[r.utente_id] || '#007bff' }}></span>
                          {r.utente_nome}
                        </td>
                        <td>{r.num_turni}</td>
                        <td><strong>{Number(r.ore_totali).toFixed(1)}h</strong></td>
                        <td>{Number(r.ore_medie_turno).toFixed(1)}h</td>
                        {isAdmin && <td>{r.costo_orario ? `€${Number(r.costo_orario).toFixed(2)}/h` : '€0.00/h'}</td>}
                        {isAdmin && <td><strong>{r.costo_totale ? `€${Number(r.costo_totale).toFixed(2)}` : '€0.00'}</strong></td>}
                      </tr>
                    ))}
                    <tr className="riepilogo-totale">
                      <td><strong>TOTALE</strong></td>
                      <td><strong>{riepilogoOre.reduce((s, r) => s + Number(r.num_turni), 0)}</strong></td>
                      <td><strong>{riepilogoOre.reduce((s, r) => s + Number(r.ore_totali), 0).toFixed(1)}h</strong></td>
                      <td>-</td>
                      {isAdmin && <td>-</td>}
                      {isAdmin && (
                        <td>
                          <strong>
                            €{riepilogoOre.reduce((s, r) => s + Number(r.costo_totale || 0), 0).toFixed(2)}
                          </strong>
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
            <div className="turno-modal-footer">
              <div className="footer-right">
                <button className="btn-turno-action btn-outline" onClick={() => setShowRiepilogoModal(false)}>Chiudi</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== MODAL: Scambi ============== */}
      {showScambiModal && (
        <div className="turno-modal-overlay" onClick={() => setShowScambiModal(false)}>
          <div className="turno-modal turno-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="turno-modal-header">
              <h2>Scambi Turno</h2>
              <button className="modal-close" onClick={() => setShowScambiModal(false)}><X size={20} /></button>
            </div>
            <div className="turno-modal-body">
              {scambi.length === 0 ? (
                <p className="no-data">Nessuna richiesta di scambio</p>
              ) : (
                <div className="scambi-list">
                  {scambi.map(s => (
                    <div key={s.id} className={`scambio-card scambio-${s.stato}`}>
                      <div className="scambio-info">
                        <div className="scambio-header">
                          <strong>{s.richiedente_nome}</strong>
                          <ArrowLeftRight size={14} />
                          <strong>{s.destinatario_nome}</strong>
                          <span className={`scambio-stato stato-${s.stato}`}>{s.stato.replace('_', ' ')}</span>
                        </div>
                        <div className="scambio-dettagli">
                          {moment(s.turno_data).format('DD/MM/YYYY')} • {s.turno_ora_inizio?.substring(0,5)}-{s.turno_ora_fine?.substring(0,5)}
                          {s.punto_vendita_nome && ` • ${s.punto_vendita_nome}`}
                        </div>
                        {s.nota_richiedente && <div className="scambio-nota">Nota: {s.nota_richiedente}</div>}
                        {s.nota_risposta && <div className="scambio-nota">Risposta: {s.nota_risposta}</div>}
                      </div>
                      <div className="scambio-actions">
                        {/* Il destinatario può accettare/rifiutare */}
                        {s.stato === 'in_attesa' && s.destinatario_id === user?.id && (
                          <>
                            <button className="btn-turno-action btn-success-sm" onClick={() => handleRispondiScambio(s.id, true)}><Check size={14} /> Accetta</button>
                            <button className="btn-turno-action btn-danger-sm" onClick={() => handleRispondiScambio(s.id, false)}><X size={14} /> Rifiuta</button>
                          </>
                        )}
                        {/* Admin/manager approva dopo accettazione */}
                        {s.stato === 'accettato' && canEdit && (
                          <>
                            <button className="btn-turno-action btn-success-sm" onClick={() => handleApprovaScambio(s.id, true)}><Check size={14} /> Approva</button>
                            <button className="btn-turno-action btn-danger-sm" onClick={() => handleApprovaScambio(s.id, false)}><X size={14} /> Rifiuta</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="turno-modal-footer">
              <div className="footer-right">
                <button className="btn-turno-action btn-outline" onClick={() => setShowScambiModal(false)}>Chiudi</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== MODAL: Dettaglio Turno (non-admin) ============== */}
      {showDetailModal && selectedTurnoDetail && (
        <div className="turno-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="turno-modal" onClick={e => e.stopPropagation()}>
            <div className="turno-modal-header">
              <h2>Dettaglio Turno</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}><X size={20} /></button>
            </div>
            <div className="turno-modal-body">
              <div className="turno-detail-grid">
                <div className="turno-detail-row">
                  <span className="turno-detail-label">Dipendente</span>
                  <span className="turno-detail-value">
                    <span className="legenda-dot" style={{ backgroundColor: coloriMap[selectedTurnoDetail.utente_id] || selectedTurnoDetail.utente_colore || '#007bff' }}></span>
                    {selectedTurnoDetail.utente_nome}
                  </span>
                </div>
                <div className="turno-detail-row">
                  <span className="turno-detail-label">Data</span>
                  <span className="turno-detail-value">{moment(selectedTurnoDetail.data).format('dddd DD MMMM YYYY')}</span>
                </div>
                <div className="turno-detail-row">
                  <span className="turno-detail-label">Orario</span>
                  <span className="turno-detail-value">{selectedTurnoDetail.ora_inizio?.substring(0,5)} - {selectedTurnoDetail.ora_fine?.substring(0,5)}</span>
                </div>
                {selectedTurnoDetail.mansione_nome && (
                  <div className="turno-detail-row">
                    <span className="turno-detail-label">Mansione</span>
                    <span className="turno-detail-value">{selectedTurnoDetail.mansione_nome}</span>
                  </div>
                )}
                {selectedTurnoDetail.punto_vendita_nome && (
                  <div className="turno-detail-row">
                    <span className="turno-detail-label">Punto Vendita</span>
                    <span className="turno-detail-value">{selectedTurnoDetail.punto_vendita_nome}</span>
                  </div>
                )}
                <div className="turno-detail-row">
                  <span className="turno-detail-label">Stato</span>
                  <span className="turno-detail-value">
                    {(() => { const si = STATI_TURNO.find(s => s.value === selectedTurnoDetail.stato); return si ? <span style={{ color: si.colore }}>● {si.label}</span> : selectedTurnoDetail.stato; })()}
                  </span>
                </div>
                {selectedTurnoDetail.pausa_inizio && selectedTurnoDetail.pausa_fine && (
                  <div className="turno-detail-row">
                    <span className="turno-detail-label">Pausa</span>
                    <span className="turno-detail-value">{selectedTurnoDetail.pausa_inizio.substring(0,5)} - {selectedTurnoDetail.pausa_fine.substring(0,5)} ({selectedTurnoDetail.pausa_minuti} min)</span>
                  </div>
                )}
                {selectedTurnoDetail.note && (
                  <div className="turno-detail-row">
                    <span className="turno-detail-label">Note</span>
                    <span className="turno-detail-value">{selectedTurnoDetail.note}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="turno-modal-footer">
              <div className="footer-left">
                {selectedTurnoDetail.utente_id === user?.id && (
                  <button className="btn-turno-action btn-outline" onClick={() => { setShowDetailModal(false); handleProponiScambio(selectedTurnoDetail); }}>
                    <ArrowLeftRight size={16} /> Proponi scambio
                  </button>
                )}
              </div>
              <div className="footer-right">
                <button className="btn-turno-action btn-outline" onClick={() => setShowDetailModal(false)}>Chiudi</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== MODAL: Proponi Scambio ============== */}
      {showScambioCreateModal && turnoPerScambio && (
        <div className="turno-modal-overlay" onClick={() => setShowScambioCreateModal(false)}>
          <div className="turno-modal" onClick={e => e.stopPropagation()}>
            <div className="turno-modal-header">
              <h2>Proponi Scambio Turno</h2>
              <button className="modal-close" onClick={() => setShowScambioCreateModal(false)}><X size={20} /></button>
            </div>
            <div className="turno-modal-body">
              <div className="scambio-info-turno">
                <p><strong>Turno:</strong> {moment(turnoPerScambio.data).format('DD/MM/YYYY')} • {turnoPerScambio.ora_inizio?.substring(0,5)}-{turnoPerScambio.ora_fine?.substring(0,5)}</p>
                {turnoPerScambio.mansione_nome && <p><strong>Mansione:</strong> {turnoPerScambio.mansione_nome}</p>}
              </div>
              <div className="form-group">
                <label>Scambia con *</label>
                <select value={formScambio.destinatario_id} onChange={e => setFormScambio({ ...formScambio, destinatario_id: e.target.value })}>
                  <option value="">Seleziona collega...</option>
                  {utenti.filter(u => u.id !== user?.id).map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Messaggio</label>
                <textarea value={formScambio.nota_richiedente} onChange={e => setFormScambio({ ...formScambio, nota_richiedente: e.target.value })} rows={3} placeholder="Motivo dello scambio..." />
              </div>
            </div>
            <div className="turno-modal-footer">
              <div className="footer-right">
                <button className="btn-turno-action btn-outline" onClick={() => setShowScambioCreateModal(false)}>Annulla</button>
                <button className="btn-turno-action btn-primary" onClick={handleSaveScambio}>
                  <ArrowLeftRight size={16} /> Proponi Scambio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TurnisticaPage;
